import { AlertTriangle, ArrowRight, CalendarClock, ShieldAlert } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { DateValue } from '@/components/common'
import type { TransferDecisionContext } from '@/features/employee'

/**
 * The three things a transfer form does not say.
 *
 * <p>Blocking credentials first and in red, because the guard will refuse the approval on
 * them — an approver who finds that out by pressing Approve has to work out why from an
 * error message.
 */
export function TransferContext({ detail }: { detail: TransferDecisionContext }) {
  return (
    <div className="space-y-4">
      {detail.blockingCredentials.length > 0 && (
        <Card className="border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-danger)]">
            <ShieldAlert className="size-4" aria-hidden="true" />
            Approving this will be refused
          </h3>
          <ul className="mt-2 space-y-1 text-sm">
            {detail.blockingCredentials.map((issue) => (
              <li key={issue.ruleId}>
                <span className="font-medium">{issue.documentTypeName}</span>
                <span className="text-[var(--color-body)]">
                  {' '}— {issue.status.toLowerCase()}
                  {issue.expiryDate ? ` on ${issue.expiryDate}` : ''} ({issue.ruleName})
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-[var(--color-danger)]">
            Fix the document, or record an override against the employee before approving.
          </p>
        </Card>
      )}

      {detail.credentialWarnings.length > 0 && (
        <Card className="border border-[var(--color-warning)] bg-[var(--color-tint-orange)] p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-warning)]">
            <AlertTriangle className="size-4" aria-hidden="true" />
            Worth knowing, but not blocking
          </h3>
          <ul className="mt-2 space-y-1 text-sm">
            {detail.credentialWarnings.map((issue) => (
              <li key={issue.ruleId}>
                <span className="font-medium">{issue.documentTypeName}</span>
                <span className="text-[var(--color-body)]"> — {issue.status.toLowerCase()}</span>
                {issue.overridden && (
                  <span className="ms-2 rounded-full bg-white/60 px-2 py-0.5 text-xs">overridden</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <CalendarClock className="size-4" aria-hidden="true" />
          When it takes effect
        </h3>
        <p className="mt-2 text-sm">
          <DateValue value={detail.effectiveFrom} />
          <span className="text-[var(--color-muted)]">
            {' '}— {detail.daysFromToday === 0
              ? 'today'
              : detail.daysFromToday > 0
                ? `in ${detail.daysFromToday} days`
                : `${Math.abs(detail.daysFromToday)} days ago`}
          </span>
        </p>
        {detail.backdated && (
          <p role="alert" className="mt-2 text-sm font-medium text-[var(--color-warning)]">
            This is backdated. It changes org attributes over a period attendance and payroll
            may already have been calculated from.
          </p>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">Cost centre</h3>
        {detail.costCentreChanges ? (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-[var(--color-muted)] line-through">{detail.fromCostCentre ?? '—'}</span>
            <ArrowRight className="size-3 text-[var(--color-muted)]" aria-hidden="true" />
            <span className="font-medium text-[var(--color-heading)]">{detail.toCostCentre ?? '—'}</span>
            <span className="text-xs text-[var(--color-warning)]">
              this is a budget move, not only a desk move
            </span>
          </p>
        ) : (
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            Unchanged — {detail.toCostCentre ?? 'none set'}.
          </p>
        )}
        <p className="mt-3 text-xs text-[var(--color-muted)]">
          {detail.costImpactReason ?? 'Cost impact is not calculated.'}
        </p>
      </Card>
    </div>
  )
}
